import React, { ReactNode, useState } from 'react';

import { Button, Collapse, Divider, Group, Paper, Stack, Text, Title } from '@mantine/core';
import { IconLock, IconTag } from '@tabler/icons-react';
import BillingPlanSummary from 'components/SalesPortal/BillingPlanSummary';
import PackageSummary from 'components/SalesPortal/PackageSummary';
import PackageTypeDescription from 'components/SalesPortal/PackageTypeDescription';
import { useLocalization } from 'context/localization-context';
import {
  BillingPlanRetrieve,
  BillingPlanWithInstallments,
  DiscountRetrieve,
  GymTitle,
  SalesPortalPackageRetrieve,
} from 'types';
import { PackageTypeLabelsEnum } from 'types/enums';
import {
  getAttendanceRuleSummary,
  getEndConditions,
  hasUniformRemainingInstallments,
  sortInstallmentsByNumber,
} from 'utils/billing.helpers';
import { getSuccessorStartDate } from 'utils/date-functions';
import { formatDiscountValue } from 'utils/discount.helpers';

import PackageDescription from './PackageDescription';

export interface OrderSummaryProps {
  gym: GymTitle | undefined;
  packageData: SalesPortalPackageRetrieve;
  billingPlan: BillingPlanRetrieve | BillingPlanWithInstallments;
  taxRate: number;
  appliedDiscount?: DiscountRetrieve | null;
  /** DiscountCodeInput slot — connected by OrderSummaryPanel, omitted in stories. */
  discountSlot?: ReactNode;
  /** Review-details block (dates, athlete, payment method) — fills in as steps complete. */
  detailsSlot?: ReactNode;
  showImmediateChargeWarning?: boolean;
  allStepsCompleted: boolean;
  isSubmitting?: boolean;
  is3DSAuthenticating?: boolean;
  onConfirm: () => void;
}

// The persistent Order Summary sidebar (spec Items 7/8): totals stay visible from
// Step 1, recalculate live, and carry the three surfaces the old Review step
// rendered (details block, successor package block, immediate-charge warning).
// Tax display follows the gym's configured inclusive/exclusive mode via the
// localization override — never a country branch.
const OrderSummary = ({
  gym,
  packageData,
  billingPlan,
  taxRate,
  appliedDiscount,
  discountSlot,
  detailsSlot,
  showImmediateChargeWarning = false,
  allStepsCompleted,
  isSubmitting = false,
  is3DSAuthenticating = false,
  onConfirm,
}: OrderSummaryProps): JSX.Element => {
  const { copy, dateFormat, formatCurrency, calculateInvoiceItemValues, calculateInvoiceSummary } =
    useLocalization(gym?.country, { isTaxInclusive: gym?.isTaxInclusive });
  const [isScheduleOpened, setIsScheduleOpened] = useState(false);

  const taxRateValue = taxRate * 100;
  const discountValue = appliedDiscount
    ? appliedDiscount.valueType === 'PERCENTAGE'
      ? Number(appliedDiscount.percentage ?? 0)
      : appliedDiscount.amount ?? 0
    : undefined;
  const discountType = appliedDiscount?.valueType;

  const endConditions = getEndConditions(packageData.attendanceRules, packageData.type);

  const installments = sortInstallmentsByNumber(billingPlan.installments);
  const summaryItems = installments.map(installment => ({
    itemPrice: installment.price,
    quantity: 1,
    taxRateValue,
    // Sales-portal discounts always apply to the first invoice only (PROD-69 locked decision)
    discountValue: installment.installmentNumber === 1 ? discountValue : undefined,
    discountType: installment.installmentNumber === 1 ? discountType : undefined,
  }));
  const { total, subtotal, taxAmount } = calculateInvoiceSummary(summaryItems);

  const scheduleRows = installments.map((installment, index) => {
    const { total: amount, discountAmount } = calculateInvoiceItemValues(summaryItems[index]);
    const originalAmount =
      discountAmount > 0
        ? calculateInvoiceItemValues({ itemPrice: installment.price, quantity: 1, taxRateValue }).total
        : null;
    return {
      installmentId: installment.installmentId,
      installmentNumber: installment.installmentNumber,
      amount,
      originalAmount,
    };
  });
  const dueToday = scheduleRows.find(row => row.installmentNumber === 1)?.amount ?? 0;
  const remainingRows = scheduleRows.slice(1);
  const remainingCount = remainingRows.length;
  const recurringAmount = remainingRows[0]?.amount ?? 0;
  const isUniformSchedule = hasUniformRemainingInstallments(installments);
  const intervalCount = billingPlan.installmentIntervalCount ?? 1;
  const intervalLabel = billingPlan.installmentInterval
    ? intervalCount > 1
      ? `${intervalCount} ${billingPlan.installmentInterval}s`
      : billingPlan.installmentInterval
    : null;

  const successorPackage = packageData.successorPackage;
  const successorBillingPlan = packageData.successorBillingPlan;
  const successorStartDate =
    successorPackage && successorBillingPlan
      ? getSuccessorStartDate(dateFormat, packageData.attendanceRules, packageData.startDate || undefined)
      : null;

  const frequencyLabel =
    billingPlan.numberOfInstallments > 1 ? (intervalLabel ? `Every ${intervalLabel}` : 'Recurring') : 'Once';

  return (
    <Paper shadow='xs' radius='lg' withBorder p='md' data-cy='orderSummary'>
      <Stack spacing='sm'>
        <Title order={3} fw={600}>
          Order Summary
        </Title>

        <Stack spacing={2}>
          <Text fw={600}>{packageData.name}</Text>
          <Text size='sm' color='dimmed'>
            {billingPlan.name}
          </Text>
          <Text size='sm'>{PackageTypeLabelsEnum[packageData.type]}</Text>
          <PackageTypeDescription type={packageData.type} size='xs' />
          {endConditions && (
            <Text size='sm' data-cy='orderSummaryEndConditions'>
              {endConditions}
            </Text>
          )}
          {getAttendanceRuleSummary(packageData.attendanceRules).map(rule => (
            <Text key={rule.key} size='sm' data-cy='orderSummaryRule'>
              {`${rule.name}: ${rule.summary}`}
            </Text>
          ))}
          {packageData.description && <PackageDescription description={packageData.description} />}
        </Stack>

        <Divider />

        <Stack spacing={3}>
          <Group position='apart'>
            <Text size='sm' color='dark.8'>
              Frequency
            </Text>
            <Text size='sm'>{frequencyLabel}</Text>
          </Group>
          <Group position='apart'>
            <Text size='sm' color='dark.8'>
              Installments
            </Text>
            <Text size='sm'>{billingPlan.numberOfInstallments}</Text>
          </Group>
          <Group position='apart'>
            <Text size='sm' color='dark.8'>
              {copy.financial.subtotal}
            </Text>
            <Text size='sm' data-cy='orderSummarySubtotal'>
              {formatCurrency(subtotal / 100)}
            </Text>
          </Group>
          <Group position='apart'>
            <Text size='sm' color='dark.8'>
              {copy.financial.tax}
            </Text>
            <Text size='sm' data-cy='orderSummaryTax'>
              {formatCurrency(taxAmount / 100)}
            </Text>
          </Group>
          {appliedDiscount && (
            <Group position='apart'>
              <Group spacing='xs'>
                <IconTag size='1rem' color='#4AC360' />
                <Text size='sm' color='green' fw={600}>
                  {appliedDiscount.name} {formatDiscountValue(appliedDiscount, formatCurrency)}
                </Text>
              </Group>
              <Text size='sm' color='green'>
                applied
              </Text>
            </Group>
          )}
          <Group position='apart'>
            <Text size='sm' color='dark.8'>
              Total
            </Text>
            <Text size='sm' data-cy='orderSummaryTotal'>
              {formatCurrency(total / 100)}
            </Text>
          </Group>
        </Stack>

        {discountSlot}

        <Divider />

        <Group position='apart'>
          <Title order={4} fw={600}>
            Due today
          </Title>
          <Title order={4} fw={600} data-cy='orderSummaryDueToday'>
            {formatCurrency(dueToday / 100)}
          </Title>
        </Group>
        {remainingCount > 0 && isUniformSchedule && (
          <Text size='xs' color='dimmed' data-cy='orderSummarySchedulePreview'>
            Then {remainingCount} payment{remainingCount > 1 ? 's' : ''} of{' '}
            {formatCurrency(recurringAmount / 100)}
            {intervalLabel ? ` every ${intervalLabel}` : ''}.
          </Text>
        )}
        {remainingCount > 0 && !isUniformSchedule && (
          <Stack spacing={4}>
            <Text size='xs' color='dimmed' data-cy='orderSummarySchedulePreview'>
              Then {remainingCount} more payments{intervalLabel ? ` every ${intervalLabel}` : ''}.
            </Text>
            <Button
              variant='subtle'
              compact
              size='xs'
              px={0}
              sx={{ alignSelf: 'flex-start' }}
              aria-expanded={isScheduleOpened}
              onClick={() => setIsScheduleOpened(opened => !opened)}
              data-cy='orderSummaryScheduleToggle'
            >
              {isScheduleOpened ? 'Hide full payment schedule' : 'Show full payment schedule'}
            </Button>
            <Collapse in={isScheduleOpened} data-cy='orderSummarySchedule'>
              <Stack spacing={2}>
                {scheduleRows.map(row => (
                  <Group key={row.installmentId} position='apart' data-cy='orderSummaryScheduleRow'>
                    <Text size='xs' color='dark.8'>
                      Installment {row.installmentNumber}
                    </Text>
                    <Group spacing='xs'>
                      {row.originalAmount !== null && (
                        <Text size='xs' color='dimmed' strikethrough>
                          {formatCurrency(row.originalAmount / 100)}
                        </Text>
                      )}
                      <Text size='xs' color={row.originalAmount !== null ? 'green' : 'dark.8'}>
                        {formatCurrency(row.amount / 100)}
                      </Text>
                    </Group>
                  </Group>
                ))}
              </Stack>
            </Collapse>
          </Stack>
        )}

        {detailsSlot}

        {successorPackage && successorBillingPlan && (
          <Stack spacing='xs' data-cy='orderSummarySuccessorPackage'>
            <Divider />
            <Text color='dark.8' fw={600}>
              Following Package and Billing Plan
            </Text>
            <PackageSummary pckg={{ ...successorPackage, startDate: successorStartDate }} />
            <BillingPlanSummary
              gym={gym}
              billingPlan={successorBillingPlan}
              taxRate={successorPackage.taxRate?.rate || 0}
            />
          </Stack>
        )}

        {showImmediateChargeWarning && (
          <Text color='red' size='sm' fs='italic' data-cy='orderSummaryImmediateChargeWarning'>
            The first invoice on this package is due today and will be charged immediately.
          </Text>
        )}

        {is3DSAuthenticating && (
          <Text size='sm' color='dimmed' data-cy='orderSummary3dsPending'>
            Verifying your payment method with your bank — please don&apos;t close this page. You can cancel
            the verification from your bank&apos;s prompt to come back and try a different payment method.
          </Text>
        )}

        <Button
          size='md'
          data-cy='confirmPurchaseButton'
          disabled={!allStepsCompleted || isSubmitting || is3DSAuthenticating}
          loading={isSubmitting || is3DSAuthenticating}
          onClick={onConfirm}
        >
          Confirm purchase
        </Button>
        {!allStepsCompleted && (
          <Text size='xs' color='dimmed' align='center' data-cy='confirmPurchaseHelper'>
            Complete all steps to place your order
          </Text>
        )}

        <Group spacing='xs' position='center'>
          <IconLock size='0.9rem' />
          <Text size='xs' color='dimmed'>
            Payments are securely processed by Stripe
          </Text>
        </Group>
      </Stack>
    </Paper>
  );
};

export default OrderSummary;
